import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Button } from '@/components/ui/button';

interface YoYPMCChartProps {
  /** Full historical PMC data (ideally 2+ years) */
  data: Array<{ date: string; ctl: number; atl: number; tsb: number }>;
}

interface AlignedPoint {
  dayOfYear: number;
  dateLabel: string;
  currentCtl?: number;
  previousCtl?: number;
  currentTsb?: number;
  previousTsb?: number;
}

type Metric = 'ctl' | 'tsb';

export function YoYPMCChart({ data }: YoYPMCChartProps) {
  const [metric, setMetric] = useState<Metric>('ctl');

  const chartData = useMemo(() => {
    if (!data.length) return [];

    const now = new Date();
    const currentYear = now.getFullYear();
    const previousYear = currentYear - 1;

    const currentYearData = data.filter((d) => d.date.startsWith(String(currentYear)));
    const previousYearData = data.filter((d) => d.date.startsWith(String(previousYear)));

    if (!currentYearData.length && !previousYearData.length) return [];

    // Build maps keyed by day-of-year (1-366)
    const toDayOfYear = (dateStr: string): number => {
      const d = new Date(dateStr);
      const start = new Date(d.getFullYear(), 0, 0);
      return Math.floor((d.getTime() - start.getTime()) / 86400000);
    };

    const currentMap = new Map<number, { ctl: number; tsb: number }>();
    currentYearData.forEach((d) => {
      currentMap.set(toDayOfYear(d.date), { ctl: d.ctl, tsb: d.tsb });
    });

    const previousMap = new Map<number, { ctl: number; tsb: number }>();
    previousYearData.forEach((d) => {
      previousMap.set(toDayOfYear(d.date), { ctl: d.ctl, tsb: d.tsb });
    });

    // Align on day-of-year
    const maxDay = Math.max(
      ...[...currentMap.keys()],
      ...[...previousMap.keys()],
    );

    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const points: AlignedPoint[] = [];

    for (let day = 1; day <= maxDay; day++) {
      const curr = currentMap.get(day);
      const prev = previousMap.get(day);
      if (!curr && !prev) continue;

      // Approximate month label
      const approxDate = new Date(currentYear, 0, day);
      const label = `${months[approxDate.getMonth()]} ${approxDate.getDate()}`;

      points.push({
        dayOfYear: day,
        dateLabel: label,
        currentCtl: curr?.ctl,
        previousCtl: prev?.ctl,
        currentTsb: curr?.tsb,
        previousTsb: prev?.tsb,
      });
    }

    return points;
  }, [data]);

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Year-over-Year Comparison</h3>
        <div className="h-[280px] flex items-center justify-center text-muted-foreground text-sm">
          Need 2+ years of data for year-over-year comparison.
        </div>
      </div>
    );
  }

  const currentYear = new Date().getFullYear();
  const metricLabels: Record<Metric, { current: string; previous: string }> = {
    ctl: { current: `${currentYear} CTL`, previous: `${currentYear - 1} CTL` },
    tsb: { current: `${currentYear} TSB`, previous: `${currentYear - 1} TSB` },
  };

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-semibold">Year-over-Year Comparison</h3>
        <div className="flex items-center gap-1">
          <Button variant={metric === 'ctl' ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-2.5" onClick={() => setMetric('ctl')}>CTL</Button>
          <Button variant={metric === 'tsb' ? 'default' : 'outline'} size="sm" className="h-7 text-xs px-2.5" onClick={() => setMetric('tsb')}>TSB</Button>
        </div>
      </div>

      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis
              dataKey="dateLabel"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              interval="preserveStartEnd"
              minTickGap={50}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }}
              width={35}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Line
              dataKey={metric === 'ctl' ? 'currentCtl' : 'currentTsb'}
              stroke="hsl(var(--chart-1))"
              strokeWidth={1.5}
              dot={false}
              name={metricLabels[metric].current}
              connectNulls
            />
            <Line
              dataKey={metric === 'ctl' ? 'previousCtl' : 'previousTsb'}
              stroke="hsl(var(--chart-4))"
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              name={metricLabels[metric].previous}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-muted-foreground text-center mt-2">
        Solid = current year · Dashed = previous year
      </p>
    </div>
  );
}
